/**
 * Where the offline Hebrew voice lives in the browser, and how it gets there.
 *
 * Mirrors mobile's `modelStore.ts` — same asset names, same folder-shaped
 * bucket, same manifest-first download so the two platforms cannot drift
 * into disagreeing about what "downloaded" means. The storage itself is the
 * browser's Cache API instead of the filesystem: content-addressed by
 * request URL, available to both the page and the synthesis worker, and
 * already how the platform expects large binary assets to be cached for
 * offline use.
 */

export type Asset = 'manifest.json' | 'assets.json' | 'model.onnx' | 'renikud.onnx'

export interface Manifest {
  version: number
  model: { file: string; bytes: number; sha256: string }
  assets: { file: string; bytes: number; sha256: string }
  sampleRate: number
  voices: string[]
  voiceLanguages?: Record<string, string>
}

export interface DownloadProgress {
  stage: Asset
  completed: number
  total: number
  fraction: number
}

/**
 * Where the models are served from.
 *
 * Upstream's Hugging Face repo, pinned to revision `110f525` (2026-09-23, the
 * commit that corrected the model card to CC BY 4.0) rather than `main`, so a
 * later upload upstream cannot change the weights under a live build. iOS and
 * Android pin the same revision. Override with `VITE_POCKET_TTS_MODELS_URL`
 * to serve from baba's own mirror.
 */
export const DEFAULT_MODELS_URL =
  (import.meta.env.VITE_POCKET_TTS_MODELS_URL as string | undefined) ||
  'https://huggingface.co/thewh1teagle/pocket-tts-onnx/resolve/110f5251188e9407b4f724acdd8b0dfd0b20692a/he/'

/**
 * What each asset is filed under in the Cache API, whatever URL it came from.
 *
 * This is the `main` URL the files were fetched from before the pin. The `he/`
 * files have not changed upstream since 2026-08-29, so a copy downloaded then
 * is the pinned revision byte for byte; keeping the key means nobody who
 * already has the voice is made to fetch 199 MB again.
 */
const CACHE_KEY_BASE = 'https://huggingface.co/thewh1teagle/pocket-tts-onnx/resolve/main/he/'

export const CACHE_NAME = 'baba-pocket-tts-he-v1'

/** LocalStorage flag the Settings row and Translator read for a cheap sync hint. */
export const READY_FLAG_KEY = 'baba.offline_voice.ready'

const ASSETS: Asset[] = ['manifest.json', 'assets.json', 'model.onnx', 'renikud.onnx']
/** `isReady` doesn't require the manifest — it only carries download sizes. */
const REQUIRED_ASSETS: Asset[] = ['assets.json', 'model.onnx', 'renikud.onnx']

/** The Cache API key an asset is stored and looked up under. */
export const assetUrl = (asset: Asset): string => `${CACHE_KEY_BASE}${asset}`

async function openCache(): Promise<Cache> {
  return caches.open(CACHE_NAME)
}

async function cachedResponse(asset: Asset): Promise<Response | undefined> {
  const cache = await openCache()
  return cache.match(assetUrl(asset))
}

/** Everything synthesis needs, already cached. */
export async function isReady(): Promise<boolean> {
  if (typeof caches === 'undefined') return false
  const cache = await openCache()
  const present = await Promise.all(REQUIRED_ASSETS.map((asset) => cache.match(assetUrl(asset))))
  return present.every(Boolean)
}

/** Cheap, synchronous hint for UI that can't await — mirrors the flag `download()` sets. */
export function isReadySync(): boolean {
  try {
    return localStorage.getItem(READY_FLAG_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Guarded readiness check every caller must use before falling through to a
 * network fallback — `isReady()` on its own is not safe to gate a fallback
 * path on.
 *
 * A caller like the Translator's "Listen" button used to `await isReady()`
 * directly. When the Cache API was slow or wedged (private-browsing storage
 * limits, a stuck service worker, a browser bug), that await never settled
 * — and because it sat before the sign-in/paywall/network-voice branches,
 * the whole button went dead: no modal, no paywall, no request, nothing.
 *
 * Two guards close that off:
 *  1. `isReadySync()` first, the cheap localStorage flag `download()` sets.
 *     If it says the voice was never downloaded there is nothing to
 *     confirm, so the real Cache API check is skipped entirely — this is
 *     also the common case (most users haven't downloaded the offline
 *     voice), so most calls never touch `caches` at all.
 *  2. When the flag says downloaded, confirm with the real check, but
 *     race it against a timeout so a stuck Cache API can never block the
 *     fallback path again.
 */
export async function isReadyGuarded(timeoutMs = 800): Promise<boolean> {
  if (!isReadySync()) return false
  return new Promise<boolean>((resolve) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      resolve(false)
    }, timeoutMs)
    isReady()
      .then((result) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve(result)
      })
      .catch(() => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve(false)
      })
  })
}

export async function readManifest(): Promise<Manifest | null> {
  const response = await cachedResponse('manifest.json')
  if (!response) return null
  try {
    return (await response.clone().json()) as Manifest
  } catch {
    return null
  }
}

export interface DownloadHandle {
  /** Resolves when every asset is cached. */
  done: Promise<void>
}

/**
 * Fetch whatever is missing, reporting progress across the whole set.
 *
 * Already-cached files are skipped, so an interrupted run resumes at file
 * granularity even if the connection dropped somewhere in the ~199 MB.
 * Cancel by aborting `signal` — a partial file is never cached, so a
 * cancelled download leaves no half-written asset behind.
 */
export function download(
  onProgress: (progress: DownloadProgress) => void = () => {},
  signal?: AbortSignal,
  baseUrl: string = DEFAULT_MODELS_URL,
): DownloadHandle {
  const run = async (): Promise<void> => {
    const cache = await openCache()

    const missing: Asset[] = []
    for (const asset of ASSETS) {
      if (!(await cache.match(assetUrl(asset)))) missing.push(asset)
    }
    if (missing.length === 0) {
      localStorage.setItem(READY_FLAG_KEY, '1')
      return
    }

    // The manifest carries the real sizes, so fetch it first and let it
    // drive one honest bar rather than four that each restart at zero.
    if (missing.includes('manifest.json')) {
      await fetchOne(cache, 'manifest.json', baseUrl, 0, 0, () => {}, signal)
    }
    const manifest = await readManifest()
    const expected: Partial<Record<Asset, number>> = manifest
      ? { 'model.onnx': manifest.model.bytes, 'assets.json': manifest.assets.bytes }
      : {}

    // renikud.onnx has no declared size in the manifest (mobile's downloader
    // has the same gap — see modelStore.ts there), so it contributes 0 to
    // `total` and the bar holds briefly while it lands. Not fixed here to
    // keep behaviour identical to mobile's.
    const total = missing.reduce((sum, asset) => sum + (expected[asset] ?? 0), 0)
    let done = 0

    for (const asset of missing) {
      if (asset === 'manifest.json') continue
      signal?.throwIfAborted()
      const base = done
      await fetchOne(cache, asset, baseUrl, base, total, onProgress, signal)
      done += expected[asset] ?? 0
    }

    if (!(await isReady())) throw new Error('Offline voice download finished incomplete')
    localStorage.setItem(READY_FLAG_KEY, '1')
  }

  return { done: run() }
}

async function fetchOne(
  cache: Cache,
  asset: Asset,
  baseUrl: string,
  base: number,
  total: number,
  report: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const url = assetUrl(asset)
  const response = await fetch(`${baseUrl}${asset}`, { signal })
  if (!response.ok) throw new Error(`Could not download ${asset} (HTTP ${response.status})`)

  const declaredLength = Number(response.headers.get('content-length') ?? 0)
  const overall = total > 0 ? total : declaredLength

  if (!response.body) {
    // No streaming reader available — cache the whole response at once.
    await cache.put(url, response)
    report({ stage: asset, completed: base + declaredLength, total: overall, fraction: 1 })
    return
  }

  // Tee the stream: one half is cached whole (so `cache.put` sees the same
  // Response the page will later read), the other is walked chunk by chunk
  // purely to report progress — `fetch` gives no progress callback itself.
  const [forCache, forProgress] = response.body.tee()
  const cachePut = cache.put(
    url,
    new Response(forCache, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    }),
  )
  // A cancelled download rejects both tee'd branches — the loop below throws
  // and propagates that up through `run()` already, so silence this branch
  // here rather than surface it a second time as an unhandled rejection.
  cachePut.catch(() => {})

  const reader = forProgress.getReader()
  let received = 0
  for (;;) {
    const { done: chunkDone, value } = await reader.read()
    if (chunkDone) break
    received += value.length
    report({
      stage: asset,
      completed: base + received,
      total: overall,
      fraction: overall > 0 ? Math.min(1, (base + received) / overall) : 0,
    })
  }
  await cachePut
}

/** Remove the downloaded voice, for a "free up space" control. */
export async function removeAll(): Promise<void> {
  if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME)
  try {
    localStorage.removeItem(READY_FLAG_KEY)
  } catch {
    /* storage unavailable — nothing left to clean up */
  }
}
